import React, { useState, useRef, useEffect } from "react";
import { useToast } from "providers/ToastProvider";
import { Formik } from "formik";
import {
  Button,
  Page,
  Card,
  CalloutCard,
  TextContainer,
  TextStyle,
  Modal,
  FormLayout,
  Tooltip,
  TextField,
  ChoiceList,
  Link,
  Stack,
  Banner,
  ProgressBar,
  Collapsible,
  DescriptionList,
} from "@shopify/polaris";
import { createCheck, searchOrders, getOrder } from "services/wpApi";
import * as yup from "yup";
import { Flex, Box } from "rebass";
import { renderContent, generateSmartSubjectLine, generateSubjectPreview } from "helpers";
import { CgExternal } from "react-icons/cg";
import PhoneInput from "components/checks/PhoneInput";
import { defaultCheckContent } from "constants";
import get from "lodash/get";
import { useShop } from "providers/ShopProvider";
import { useHistory, useLocation } from "react-router-dom";
import "yup-phone";
import OrderSelectionV2 from "./checks/OrderSelectionv2";
import { motion, AnimatePresence } from "framer-motion";
import { IdCheckOption } from "components/checks/new";
import { AiOutlineIdcard } from "react-icons/ai";
import { BiFace } from "react-icons/bi";
import SignatureRequired from "components/checks/SignatureRequired";
import ProofOfAddressRequired from "components/checks/ProofOfAddressRequired";
import { getRecentSubjects, saveSubjectToHistory } from "utils/subjectHistory";

export default function NewCheck() {
  const nameFieldsRef = useRef(null);
  const setToast = useToast();
  const shop = useShop();
  const location = useLocation();

  const [firstNameFieldFocus, setFirstNameFieldFocus] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContactError, setPreviewContactError] = useState("");
  const [previewSms, setPreviewSms] = useState("");
  const [previewEmail, setPreviewEmail] = useState("");
  const history = useHistory();

  const [orders, setOrders] = useState([]);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [step, setStep] = useState(1);

  const [openSearchOrders, setOpenSearchOrders] = useState(true);
  const [preloadedOrderData, setPreloadedOrderData] = useState(null);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [contentShortcodesOpen, setContentShortcodesOpen] = useState(false);
  const [showRecentSubjects, setShowRecentSubjects] = useState(false);
  const [recentSubjects, setRecentSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [isTypingSubject, setIsTypingSubject] = useState(false);

  const scrollToRef = (ref) => {
    // ref.current.scrollIntoView({block: 'end'}
    window.scrollTo(0, ref.current.offsetTop);
  };

  /**
   * Extract query parameters from hash
   */
  const getHashParams = () => {
    // Get the full URL including hash
    const fullUrl = window.location.href;
    console.log('[NewCheck] Full URL:', fullUrl);
    console.log('[NewCheck] React location.hash:', location.hash);
    console.log('[NewCheck] Window location.hash:', window.location.hash);
    
    // Use window.location.hash instead of React Router's location.hash
    const hashWithQuery = window.location.hash;
    const questionMarkIndex = hashWithQuery.indexOf('?');
    
    if (questionMarkIndex === -1) {
      console.log('[NewCheck] No query params found in hash');
      return {};
    }
    
    const queryString = hashWithQuery.substring(questionMarkIndex + 1);
    console.log('[NewCheck] Query string extracted:', queryString);
    
    const params = new URLSearchParams(queryString);
    const result = {};
    for (let [key, value] of params) {
      result[key] = value;
      console.log(`[NewCheck] Param found: ${key} = ${value}`);
    }
    
    console.log('[NewCheck] All params:', result);
    return result;
  };

  /**
   * Check for orderId in URL and fetch order data
   */
  useEffect(() => {
    console.log('[NewCheck] useEffect triggered, window.location.hash:', window.location.hash);
    const params = getHashParams();
    const orderId = params.orderId;
    console.log('[NewCheck] Order ID from params:', orderId);
    
    if (orderId) {
      console.log('[NewCheck] Order ID found, fetching order:', orderId);
      setIsLoadingOrder(true);
      
      // Fetch specific order by ID using dedicated endpoint
      console.log('[NewCheck] Calling getOrder with ID:', orderId);
      
      getOrder(orderId)
        .then(({ data }) => {
          console.log('[NewCheck] getOrder response:', data);
          // The order data is returned directly, not wrapped in data.order
          const order = data;
          
          if (order && order.id) {
            console.log('[NewCheck] Order found:', order);
            setPreloadedOrderData(order);
            // Skip to step 2 since we have an order
            setStep(2);
            setOpenSearchOrders(false);
            setToast(`Loaded order #${orderId} details`);
          } else {
            console.log('[NewCheck] No order returned for ID:', orderId);
            setToast(`Order #${orderId} not found`);
          }
        })
        .catch((e) => {
          console.error('[NewCheck] Error fetching order:', e);
          setToast(`Unable to load order #${orderId}`);
        })
        .finally(() => {
          setIsLoadingOrder(false);
        });
    } else {
      console.log('[NewCheck] No orderId found in URL params');
    }
  }, []);

  /**
   * Search for recent orders
   */
  useEffect(() => {
    // https://github.com/woocommerce/woocommerce/wiki/wc_get_orders-and-WC_Order_Query#parameters
    searchOrders({ page, limit: 3, orderby: "ID", order: "DESC" })
      .then(({ data: { orders, max_num_pages, total } }) => {
        setOrders(orders);
        setTotalPages(max_num_pages);
      })
      .catch((e) => {
        console.log(e);
      });
  }, [page]);

  /**
   * Load recent subject lines from localStorage on mount
   */
  useEffect(() => {
    const subjects = getRecentSubjects();
    setRecentSubjects(subjects);
  }, []);

  // Get the most recent subject for initial form value
  const initialRecentSubjects = getRecentSubjects();
  const initialSubject = initialRecentSubjects.length > 0 ? initialRecentSubjects[0] : "";

  return (
    <Formik
      initialValues={{
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        content: get(shop, "settings.defaultContent", defaultCheckContent),
        wcOrderId: null,
        wcCustomerId: null,
        orderId: null,
        signatureRequired: false,
        proofOfAddressRequired: false,
        idCheckType: shop?.settings?.idCheckType || "idv",
        deliveryMethods: [],
        customSubject: initialSubject, // Initialize with most recent subject from localStorage
      }}
      validationSchema={yup.object().shape({
        firstName: yup.string(),
        lastName: yup.string(),
        // deliveryMethods: yup
        //   .array()
        //   .required("Pick one or more delivery methods")
        //   .min(1),
        // email: yup.string().when("deliveryMethods", {
        //   is: (deliveryMethods) => (deliveryMethods || []).includes("email"),
        //   then: yup.string().email("Invalid email address"),
        // }),
        // phone: yup.string().when("deliveryMethods", {
        //   is: (deliveryMethods) => deliveryMethods.includes("sms"),
        //   then: yup.string().phone(),
        // }),
        content: yup.string(),
      })}
      onSubmit={(values, { setSubmitting }) => {
        // Prepare the data to send
        const checkData = {
          ...values,
          // Only include subject if custom subject is provided
          ...(values.customSubject ? 
              { subject: values.customSubject } : {})
        };
        
        createCheck(checkData)
          .then(({ data }) => {
            // Save the custom subject to history if one was provided
            if (values.customSubject && values.customSubject.trim() !== '') {
              saveSubjectToHistory(values.customSubject);
              // Update the recent subjects list to reflect the new addition
              setRecentSubjects(getRecentSubjects());
            }
            
            setToast(data.message);
            setSubmitting(false);
            // redirect now
            history.push(`/checks/${data.check.id}`);
          })
          .catch((e) => {
            setToast("Unable to create an ID check");
            setSubmitting(false);
          });
      }}
    >
      {({
        setFieldValue,
        values,
        isSubmitting,
        errors,
        isValid,
        handleSubmit,
        handleBlur,
        handleReset: resetForm,
      }) => {
        
        // Populate form when preloadedOrderData is available
        React.useEffect(() => {
          if (preloadedOrderData && preloadedOrderData.id) {
            console.log('[NewCheck] Populating form with preloaded order data:', preloadedOrderData);
            
            // Set customer details
            const firstName = preloadedOrderData.customer?.first_name || preloadedOrderData.billing?.first_name || "";
            const lastName = preloadedOrderData.customer?.last_name || preloadedOrderData.billing?.last_name || "";
            const email = preloadedOrderData.customer?.email || preloadedOrderData.billing?.email || "";
            const phone = preloadedOrderData.customer?.phone || preloadedOrderData.billing?.phone || "";
            
            // Calculate delivery methods
            const availableDeliveryMethods = [];
            if (email) availableDeliveryMethods.push("email");
            if (phone) availableDeliveryMethods.push("sms");
            
            // Set all form fields
            setFieldValue("firstName", firstName);
            setFieldValue("lastName", lastName);
            setFieldValue("email", email);
            setFieldValue("phone", phone);
            setFieldValue("wcOrderId", preloadedOrderData.id);
            setFieldValue("wcCustomerId", preloadedOrderData.customer_id || null);
            setFieldValue("orderId", preloadedOrderData.id);
            setFieldValue("deliveryMethods", availableDeliveryMethods);
            
            // Also set the smart subject default with the order ID
            const smartSubject = generateSmartSubjectLine({
              orderId: preloadedOrderData.id,
              shopName: shop?.shopName
            });
            setFieldValue("customSubject", smartSubject);
            
            console.log('[NewCheck] Form populated with values:', {
              firstName, lastName, email, phone, 
              wcOrderId: preloadedOrderData.id,
              deliveryMethods: availableDeliveryMethods
            });
          }
        }, [preloadedOrderData, setFieldValue]);

        // Auto-populate subject field with smart default when order changes
        React.useEffect(() => {
          // Only generate smart default when an order is selected or changed
          if (!values.wcOrderId) {
            return; // Keep whatever is in the field (including localStorage value)
          }
          
          const smartDefault = generateSmartSubjectLine({
            orderId: values.wcOrderId,
            shopName: shop?.shopName
          });
          
          // Only update if:
          // 1. Field is empty
          // 2. Field contains a generic default pattern (not a custom subject)
          // 3. Field contains an old order ID pattern that needs updating
          const shouldUpdate = !values.customSubject || 
            values.customSubject === "" ||
            (values.customSubject.includes("Your recent") && 
             values.customSubject.includes("order") && 
             values.customSubject.includes("requires ID verification")) ||
            (values.customSubject.includes("order #") && !values.customSubject.includes(`#${values.wcOrderId}`));
            
          if (shouldUpdate) {
            setFieldValue("customSubject", smartDefault);
          }
        }, [values.wcOrderId, shop?.shopName, setFieldValue]);
        
        // Filter recent subjects based on current input
        React.useEffect(() => {
          if (!isTypingSubject || !values.customSubject) {
            setFilteredSubjects([]);
            setSelectedSuggestionIndex(-1);
            return;
          }
          
          const currentValue = values.customSubject.toLowerCase();
          const filtered = recentSubjects.filter(subject => 
            subject.toLowerCase().includes(currentValue) && 
            subject.toLowerCase() !== currentValue
          );
          
          setFilteredSubjects(filtered);
          setSelectedSuggestionIndex(-1);
        }, [values.customSubject, recentSubjects, isTypingSubject]);

        return (
        <Page>
          {isLoadingOrder && (
            <Banner status="info">
              Loading order details...
            </Banner>
          )}
          {preloadedOrderData && (
            <Banner status="success">
              Order #{preloadedOrderData.id} has been loaded. Customer details have been populated automatically.
            </Banner>
          )}
          <div className="ri-bg-white ri-rounded-md ri-shadow-md ri-px-8 ri-my-6 ri-mx-auto ri-py-3 ri-grid md:ri-grid-cols-2">
            <div className="ri-flex ri-flex-col ri-justify-center ri-mt-5">
              <h1 className="ri-font-bold ri-text-xl ri-tracking-wide ri-mb-4">
                Who would you like to verify?
              </h1>
              <p className="ri-mb-3">
                Start by clicking an order to populate the contact information
                for the ID check automatically.
              </p>
              <p>Or skip selecting an order and send an ID check manually.</p>
            </div>
            <img
              className="ri-justify-self-end"
              alt="Sending an ID check manually"
              src="https://res.cloudinary.com/tinyhouse/image/upload/c_scale,w_350/v1628513381/Real%20ID/New%20Check%20Page/ID_verification_illustraction_shutterstock.png"
            />
          </div>

          {/* <ProgressBar progress={0} size="large" /> */}
          <div className="ri-bg-white ri-rounded-lg ri-shadow ri-mb-16 ri-p-8">
            <h2 className="ri-text-lg ri-px-5 ri-font-semibold ri-mb-4">
              New ID check details
            </h2>
            <h3 className="ri-text-base ri-text-gray-600 ri-px-5 ri-pb-4">
              Step {step} of 5
            </h3>
            <div className="ri-mx-5 ri-mb-8">
              <ProgressBar progress={(step / 5) * 100} />
            </div>

            {step === 1 && (
              <OrderSelectionV2
                openSearchOrders={openSearchOrders}
                setOpenSearchOrders={setOpenSearchOrders}
                setStep={setStep}
              />
            )}

            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <div className="ri-p-5">
                  <h2 className="ri-text-lg ri-mb-6">
                    Enter the recipient details
                  </h2>

                  {(!!values.wcOrderId || !!values.wcCustomerId) && (
                    <p className="ri-mb-4 ri-text-blue-600">
                      {`Currently using customer details from ${
                        values?.wcOrderId
                          ? `order ${values.wcOrderId} and `
                          : ""
                      }${
                        values?.wcCustomerId
                          ? `customer ${values?.wcCustomerId}`
                          : ""
                      }.`}{" "}
                      <br />
                      <Link
                        className="ri-underline"
                        onClick={() => {
                          resetForm();
                        }}
                      >
                        Reset the selected order.
                      </Link>
                    </p>
                  )}
                  {!values.wcOrderId && !values.wcCustomerId && (
                    <>
                      <p>
                        <TextStyle variation="subdued">
                          Fill out the customers details below.
                        </TextStyle>
                      </p>

                      <div className="ri-my-6">
                        <Banner status="warning">
                          <p>
                            <TextStyle variation="warning">
                              Without an order or customer association, this ID
                              check will not be able to update the customer or
                              their order with real time meta or notes.
                            </TextStyle>
                          </p>
                          <Link
                            onClick={() => {
                              setStep(1);
                              resetForm();
                            }}
                            className="ri-underline"
                          >
                            Select an order or customer
                          </Link>
                        </Banner>
                      </div>
                    </>
                  )}
                  <div ref={nameFieldsRef} className="ri-flex ri-gap-2 ri-mb-8">
                    <TextField
                      id="first-name"
                      label="First Name"
                      value={values.firstName}
                      onChange={(value) => {
                        setFieldValue("firstName", value);
                      }}
                      focused={firstNameFieldFocus}
                      onBlur={() => {
                        setFirstNameFieldFocus(false);
                      }}
                    />
                    <TextField
                      id="last-name"
                      label="Last Name"
                      value={values.lastName}
                      onChange={(value) => {
                        setFieldValue("lastName", value);
                      }}
                    />
                  </div>
                  <h2 className="ri-text-lg ri-mt-6 ri-mb-3">
                    Choose the delivery methods
                  </h2>
                  <ChoiceList
                    allowMultiple
                    title="Select the methods for how the ID check link should be sent to the customer."
                    choices={[
                      { label: "via Text Message", value: "sms" },
                      { label: "via Email", value: "email" },
                    ]}
                    selected={values.deliveryMethods || []}
                    onChange={(value) => {
                      setFieldValue("deliveryMethods", value);
                    }}
                  />
                  <div className="ri-flex ri-flex-col ri-max-w-md ri-my-4">
                    {(values.deliveryMethods || []).includes("sms") && (
                      <div className="ri-mb-6">
                        <PhoneInput
                          phone={values.phone}
                          onChange={(value) => {
                            setFieldValue("phone", value);
                          }}
                          phoneWarning={errors?.phone}
                          onBlur={handleBlur}
                        />
                      </div>
                    )}
                    {(values.deliveryMethods || []).includes("email") && (
                      <TextField
                        id="email"
                        label="Email Address"
                        type="email"
                        value={values.email}
                        error={errors?.email}
                        onBlur={handleBlur}
                        onChange={(value) => {
                          setFieldValue("email", value);
                        }}
                        helpText="We’ll use this email address to send the secure ID check link to."
                      />
                    )}
                  </div>
                  <div className="ri-flex ri-justify-between">
                    <button
                      className="ri-bg-transparent ri-border-0 ri-text-gray-600 ri-underline ri-text-base ri-cursor-pointer hover:ri-text-gray-500"
                      onClick={() => {
                        setStep(1);
                        resetForm();
                      }}
                    >
                      Start over
                    </button>
                    <button
                      disabled={errors?.email}
                      className={`ri-text-white ri-text-base ri-px-4 ri-py-3 ri-rounded ri-border-0  ${
                        (values.email && errors?.email) ||
                        (values.phone && errors?.phone)
                          ? "ri-bg-gray-500 ri-cursor-disabled"
                          : "ri-bg-green-700 hover:ri-bg-green-800 ri-cursor-pointer"
                      }`}
                      onClick={() => setStep(3)}
                    >
                      Next: Message content &rarr;
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <div className="ri-p-5">
                  <h2 className="ri-text-base ri-font-semibold ri-mb-6">
                    Edit message content
                  </h2>
                  <p className="ri-mb-4">
                    This content will appear in the email message for this ID
                    check only.
                  </p>
                  <p className="ri-mb-4">
                    {`The customer's ID verification link will be automatically added to this content sent via ${values.deliveryMethods
                      .map((method) =>
                        method == "sms" ? "text message" : "email"
                      )
                      .join(" and ")}.`}
                  </p>
                  <p className="ri-mb-8">
                    Design the theme, branding, and default content of your ID
                    checks on{" "}
                    <Link
                      onClick={() => {
                        redirect.dispatch(Redirect.Action.APP, {
                          path: "/shopify/settings",
                          newContext: true,
                        });
                      }}
                      external
                    >
                      the settings page <CgExternal />
                    </Link>
                  </p>

                  <Box mb={4}>
                    <TextContainer>
                      <p>
                        <TextStyle variation="subdued">
                          <strong>(Optional)</strong>{" "}
                          <span 
                            className="ri-underline ri-cursor-pointer"
                            onClick={() => setContentShortcodesOpen(!contentShortcodesOpen)}
                          >
                            Use shortcodes
                          </span>
                          {" "}to personalize the message sent to the customer.
                        </TextStyle>
                      </p>
                    </TextContainer>
                    
                    <Collapsible
                      open={contentShortcodesOpen}
                      id="content-shortcodes-collapsible"
                      expandOnPrint
                    >
                      <div className="ri-mt-2">
                        <DescriptionList
                          items={[
                            {
                              term: "[orderId]",
                              description: values.wcOrderId 
                                ? `The ID of the selected order (#${values.wcOrderId})`
                                : "The order ID (no order selected - shortcode will be empty)"
                            },
                            {
                              term: "[firstName]",
                              description: values.firstName 
                                ? `The customer's first name (${values.firstName})`
                                : "The customer's first name"
                            },
                            {
                              term: "[lastName]",
                              description: values.lastName
                                ? `The customer's last name (${values.lastName})`
                                : "The customer's last name"
                            },
                          ]}
                        />
                      </div>
                    </Collapsible>
                  </Box>

                  <div className="ri-grid ri-grid-cols-2 ri-gap-4 ri-my-6">
                    <div>
                      {/* Email Subject for email delivery */}
                      {(values.deliveryMethods || []).includes('email') && (
                        <div className="ri-mb-4">
                          <h2 className="ri-text-base ri-font-semibold ri-mb-2">
                            Email subject
                          </h2>
                          <TextField
                            value={values.customSubject}
                            onChange={(value) => {
                              setFieldValue("customSubject", value);
                              setIsTypingSubject(true);
                            }}
                            onFocus={() => {
                              setIsTypingSubject(true);
                              // Show all suggestions when focused with empty or default value
                              if (!values.customSubject || 
                                  (values.customSubject.includes("Your recent") && 
                                   values.customSubject.includes("order requires ID verification"))) {
                                setFilteredSubjects(recentSubjects);
                              }
                            }}
                            onBlur={() => {
                              // Delay hiding to allow click events on suggestions
                              setTimeout(() => {
                                setIsTypingSubject(false);
                                setFilteredSubjects([]);
                              }, 200);
                            }}
                            onKeyDown={(e) => {
                              if (!filteredSubjects.length) return;
                              
                              if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                setSelectedSuggestionIndex(prev => 
                                  prev < filteredSubjects.length - 1 ? prev + 1 : prev
                                );
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
                              } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
                                e.preventDefault();
                                setFieldValue("customSubject", filteredSubjects[selectedSuggestionIndex]);
                                setIsTypingSubject(false);
                                setFilteredSubjects([]);
                                setSelectedSuggestionIndex(-1);
                              } else if (e.key === 'Escape') {
                                setIsTypingSubject(false);
                                setFilteredSubjects([]);
                                setSelectedSuggestionIndex(-1);
                              }
                            }}
                            placeholder="Enter custom subject line"
                          />
                          
                          {/* Autocomplete dropdown */}
                          {filteredSubjects.length > 0 && isTypingSubject && (
                            <div className="ri-absolute ri-z-10 ri-w-full ri-mt-1 ri-border ri-border-gray-300 ri-rounded ri-bg-white ri-shadow-lg">
                              <div className="ri-px-3 ri-py-2 ri-text-xs ri-text-gray-600 ri-border-b ri-border-gray-200">
                                Recent subject lines:
                              </div>
                              {filteredSubjects.map((subject, index) => (
                                <div
                                  key={index}
                                  className={`ri-px-3 ri-py-2 ri-text-sm ri-cursor-pointer ri-border-b ri-border-gray-100 last:ri-border-b-0 ${
                                    index === selectedSuggestionIndex 
                                      ? 'ri-bg-blue-50' 
                                      : 'hover:ri-bg-gray-50'
                                  }`}
                                  onMouseEnter={() => setSelectedSuggestionIndex(index)}
                                  onClick={() => {
                                    setFieldValue("customSubject", subject);
                                    setIsTypingSubject(false);
                                    setFilteredSubjects([]);
                                    setSelectedSuggestionIndex(-1);
                                  }}
                                >
                                  {subject}
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Warning for missing order */}
                          {values.customSubject.includes('[orderId]') && !values.wcOrderId && (
                            <Banner status="warning" className="ri-mt-2">
                              Your subject includes [orderId] but no order is selected. 
                              The order ID will be empty in the sent email.
                            </Banner>
                          )}
                        </div>
                      )}

                      <h2 className="ri-text-base ri-font-semibold ri-mb-2">
                        Message content
                      </h2>
                      <textarea
                        id="content"
                        rows="6"
                        className="ri-w-full ri-text-sm ri-text-gray-700 ri-border ri-border-gray-600 ri-border-solid ri-rounded ri-p-3 ri-font-sans ri-leading-6"
                        value={values.content}
                        onChange={(e) => {
                          setFieldValue("content", e.target.value);
                        }}
                      />
                    </div>

                    <div>
                      {/* Subject Preview for email delivery */}
                      {(values.deliveryMethods || []).includes('email') && (
                        <>
                          <h2 className="ri-text-base ri-font-semibold ri-mb-2">
                            Preview
                          </h2>
                          <div className="ri-p-3 ri-bg-gray-50 ri-rounded ri-border ri-mb-4">
                            <div className="ri-text-sm ri-text-gray-800">
                              {generateSubjectPreview(values, shop?.shopName)}
                            </div>
                          </div>
                          
                          {/* Invisible spacer to align with "Message content" heading */}
                          <div className="ri-h-7 ri-mb-2"></div>
                        </>
                      )}
                      
                      {/* Message Preview */}
                      {!(values.deliveryMethods || []).includes('email') && (
                        <h2 className="ri-text-base ri-font-semibold ri-mb-2">
                          Preview
                        </h2>
                      )}
                      
                      <div className="ri-leading-8 ri-relative ri-whitespace-pre-wrap ri-bg-blue-600 ri-text-white ri-p-6 ri-rounded-lg">
                        {renderContent(values.content, {
                          firstName: values.firstName,
                          lastName: values.lastName,
                          selectedOrder: `#${values.wcOrderId}`,
                        })}
                        <div className="ri-bg-blue-600 ri-w-4 ri-h-4 ri-absolute ri-left-0 ri--bottom-0"></div>
                      </div>
                    </div>
                  </div>

                  <div className="ri-flex ri-justify-between">
                    <button
                      className="ri-bg-transparent ri-border-0 ri-text-gray-600 ri-underline ri-text-base ri-cursor-pointer hover:ri-text-gray-500"
                      onClick={() => setStep(2)}
                    >
                      Back to recipient details
                    </button>
                    <button
                      className="ri-bg-green-700 ri-text-white ri-text-base ri-px-4 ri-py-3 ri-rounded ri-border-0 ri-cursor-pointer hover:ri-bg-green-800"
                      onClick={() => setStep(4)}
                    >
                      Next: ID check requirements &rarr;
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <div className="ri-px-5 ri-my-5">
                  <h2 className="ri-text-base ri-font-semibold">
                    ID check requirements
                  </h2>
                  <h3 className="ri-text-base">
                    Select the level of detail your customer should provide
                    during their ID check.
                  </h3>
                  <div className="ri-flex ri-my-4 ri-flex ri-gap-4">
                    <IdCheckOption
                      selected={values.idCheckType === "id"}
                      onClick={() => {
                        setFieldValue("idCheckType", "id");
                      }}
                      title="ID photo only"
                      icons={<AiOutlineIdcard className="ri-w-6 ri-h-6" />}
                      subtitle="More convenient"
                      helperText="Only require a photo of the customer's valid ID. The ID will be scanned for authenticity."
                    />
                    <IdCheckOption
                      selected={values.idCheckType === "idv"}
                      onClick={() => {
                        setFieldValue("idCheckType", "idv");
                      }}
                      icons={
                        <div className="ri-flex ri-items-center ri-mb-2">
                          <AiOutlineIdcard className="ri-w-6 ri-h-6" />
                          <span className="ri-mx-1">+</span>
                          <BiFace className="ri-w-6 ri-h-6" />
                        </div>
                      }
                      title="ID photo and headshot"
                      subtitle="More secure"
                      helperText="The face in the headshot will be cross referenced with the face photo on the ID document. Also includes the ID authenticity scan."
                    />
                  </div>
                  {/* <SignatureRequired
                    crossedOut={shop?.sandboxMode}
                    checked={values.signatureRequired}
                    setChecked={(checked) =>
                      setFieldValue("signatureRequired", checked)
                    }
                    shop={shop}
                  />
                  <ProofOfAddressRequired
                    crossedOut={shop?.sandboxMode}
                    checked={values.proofOfAddressRequired}
                    setChecked={(checked) =>
                      setFieldValue("proofOfAddressRequired", checked)
                    }
                    shop={shop}
                  /> */}
                  <div className="ri-flex ri-justify-between ri-py-5">
                    <button
                      className="ri-bg-transparent ri-border-0 ri-text-gray-600 ri-underline ri-text-base ri-cursor-pointer ri-cursor-pointer hover:ri-text-gray-500"
                      onClick={() => setStep(3)}
                    >
                      Back to message contents
                    </button>
                    <button
                      className="ri-bg-green-700 ri-text-white ri-text-base ri-px-4 ri-py-3 ri-rounded ri-border-0 ri-cursor-pointer hover:ri-bg-green-800"
                      onClick={() => setStep(5)}
                    >
                      Next: Review &rarr;
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 5 && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                <div className="ri-p-5">
                  <div className="ri-mb-4">
                    <h2 className="ri-text-base ri-font-semibold">
                      Review and send
                    </h2>
                    <p>
                      You're about to send a new ID check to {values.firstName}{" "}
                      {values.lastName}
                    </p>
                  </div>

                  <div className="ri-mb-4">
                    <h3 className="ri-text-sm ri-font-semibold">
                      How we'll notify the customer
                    </h3>
                    <ul>
                      {(values.deliveryMethods || []).map((dm) => (
                        <li>
                          Send a notification via {dm} to{" "}
                          {dm === "sms" && <strong>{values.phone}</strong>}
                          {dm === "email" && <strong>{values.email}</strong>}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Email Subject Preview */}
                  {(values.deliveryMethods || []).includes('email') && (
                    <div className="ri-mb-4">
                      <h3 className="ri-text-sm ri-font-semibold">Email Subject</h3>
                      <div className="ri-text-sm ri-text-gray-700">
                        "{generateSubjectPreview(values, shop?.shopName)}"
                      </div>
                      {values.customSubject && (
                        <div className="ri-text-xs ri-text-gray-500">Using custom subject line</div>
                      )}
                    </div>
                  )}

                  <div className="ri-mb-4">
                    <h3 className="ri-text-sm ri-font-semibold">
                      Message contents
                    </h3>
                    <div>
                      {renderContent(values.content, {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        selectedOrder: `#${values.wcOrderId}`,
                      })}
                    </div>
                  </div>

                  <div className="ri-mb-4">
                    <h3 className="ri-text-sm ri-font-semibold">E-signature</h3>
                    <p>
                      A signature{" "}
                      <strong>
                        {values.signatureRequired ? "is" : "is not"}
                      </strong>{" "}
                      required for this ID check.
                    </p>
                  </div>

                  <div className="ri-mb-4">
                    <h3 className="ri-text-sm ri-font-semibold">
                      Proof of address
                    </h3>
                    <p>
                      A proof of address document{" "}
                      <strong>
                        {values.proofOfAddressRequired ? "is" : "is not"}
                      </strong>{" "}
                      required for this ID check.
                    </p>
                  </div>

                  <div className="ri-flex ri-justify-between">
                    <button
                      className="ri-bg-transparent ri-border-0 ri-text-gray-600 ri-underline ri-text-base ri-cursor-pointer hover:ri-text-gray-500"
                      onClick={() => setStep(4)}
                    >
                      Back to ID check requirements
                    </button>
                    <button
                      className="ri-bg-green-700 ri-text-lg ri-text-white ri-text-base ri-px-8 ri-py-3 ri-rounded ri-border-0 ri-cursor-pointer hover:ri-bg-green-800"
                      onClick={() => {
                        console.log("submitting");
                        handleSubmit();
                      }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending…" : "Send check"}
                    </button>
                  </div>

                  {errors && (
                    <ul className="ri-desc ri-text-red-500">
                      {Object.values(errors).map((e) => (
                        <li>{e}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            )}
          </div>
          <Modal
            open={showPreviewModal}
            onClose={() => {
              setShowPreviewModal(false);
            }}
            title="Send a preview of the ID check message"
            primaryAction={{
              content: "Send Preview ID check",
              disabled: previewContactError,
              onAction: () => {
                api
                  .post("/api/checks/deliver/demo", {
                    deliveryMethods: values.deliveryMethods,
                    firstName,
                    lastName,
                    orderId: selectedOrder,
                    content,
                    previewEmail,
                    previewSms,
                  })
                  .then((res) => {
                    amplitudeEvent("Sent ID check preview", {
                      deliveryMethods: values.deliveryMethods,
                      firstName,
                      lastName,
                      orderId: selectedOrder,
                      content,
                      previewEmail,
                      previewSms,
                    });
                    setToast("Preview ID check sent");
                  })
                  .catch((err) => {
                    setToast(
                      "Sorry, failed to send a preview ID check. Please try again later."
                    );
                  });
              },
            }}
          >
            <Modal.Section>
              <Stack vertical>
                <Stack.Item>
                  <TextContainer>
                    <p>
                      You can preview the message sent to your customers. The
                      message will contain a preview of the content your
                      customers would see.
                    </p>
                    <Banner status="info">
                      The content in the message is the actual content sent to
                      the customer. However, the link to open the ID
                      verification check is non-functional.
                    </Banner>
                  </TextContainer>
                </Stack.Item>
                <Stack.Item fill>
                  <FormLayout.Group>
                    <PhoneInput
                      phone={previewSms}
                      onChange={(value) => {
                        setPreviewSms(value);
                      }}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField
                      label="Email"
                      type="email"
                      value={previewEmail}
                      onChange={(value) => {
                        setPreviewEmail(value);
                      }}
                      helpText="We’ll use this email address to send the ID check instructions to."
                    />
                  </FormLayout.Group>
                  {previewContactError && (
                    <TextStyle variation="negative">
                      {previewContactError}
                    </TextStyle>
                  )}
                </Stack.Item>
              </Stack>
            </Modal.Section>
          </Modal>
        </Page>
        );
      }
    }
    </Formik>
  );
}
